<template>
	<div class="karen-ai-files-manager" v-loading="uploading">
		<div class="row">
			<div class="col">
				<div class="card">
					<div class="card-header">
						<h3 class="text-muted mb-0">
							Upload files to enhance the knowledge of the AI agent
						</h3>
					</div>
					<div class="card-body">
						<form @submit.prevent="handleSubmit">
							<div class="row mb-4">
								<div class="col-md-6">
									<label class="form-label">Select master data file country:</label>
									<el-select
										v-model="selectedCountry"
										placeholder="Select a country"
										class="w-100"
										size="medium"
									>
										<el-option
											v-for="country in countries"
											:key="country.value"
											:label="country.label"
											:value="country.value"
										/>
									</el-select>
								</div>
							</div>

							<div class="row mb-4">
								<div class="col">
									<label class="form-label">Upload File</label>
									<el-upload action="dummy-url" ref="fileUpload" class="upload-demo" drag
										:auto-upload="false" :on-change="handleFileChange" :limit="1"
										:on-exceed="handleExceed" accept=".xlsx">
										<i class="el-icon-upload"></i>
										<div class="el-upload__text">
											Drop file here or <em>click to upload</em>
										</div>
										<div class="el-upload__tip" slot="tip">
											Supported formats: XLSX (max {{ allowedSize }} MB)
										</div>
									</el-upload>
								</div>
							</div>

							<div class="row">
								<div class="col text-right">
									<el-button type="primary" :loading="uploading" :disabled="!canSubmit"
										@click="handleSubmit">
										{{ uploading ? "Uploading..." : "Upload File" }}
									</el-button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Upload, Select, Option, Button, Message } from "element-ui";
import { HTTP } from "@/services";

export default {
	components: {
		[Upload.name]: Upload,
		[Select.name]: Select,
		[Option.name]: Option,
		[Button.name]: Button,
	},
	data() {
		return {
			selectedCountry: "",
			fileList: [],
			uploading: false,
            countries: [{
                value: "AU",
                label: "Australia"
            }, {
                value: "UK",
                label: "United Kingdom"
            }],
			australianStates: [
				{ value: "nsw", label: "New South Wales (NSW)" },
				{ value: "vic", label: "Victoria (VIC)" },
				{ value: "qld", label: "Queensland (QLD)" },
				{ value: "wa", label: "Western Australia (WA)" },
				{ value: "sa", label: "South Australia (SA)" },
				{ value: "tas", label: "Tasmania (TAS)" },
				{ value: "act", label: "Australian Capital Territory (ACT)" },
				{ value: "nt", label: "Northern Territory (NT)" },
			],
			allowedSize: 5
		};
	},
	computed: {
		canSubmit() {
			return this.selectedCountry?.length && this.fileList.length == 1 && ! this.uploading;
		},
	},
	methods: {
		handleFileChange(file, fileList) {
			// Check file size (5MB limit)
			const isLt5M = file.size / 1024 / 1024 < this.allowedSize;
			if (!isLt5M) {
				console.log(`File size: ${file.size / 1024 / 1024} MB`);
				Message.error(`File size cannot exceed ${this.allowedSize} MB!`);
				return false;
			}

			this.fileList = fileList;
		},

		handleExceed(files, fileList) {
			Message.warning(
				"You can only upload one file at a time. Please remove the existing file before uploading a new one."
			);
		},

		async handleSubmit() {
			if (!this.canSubmit) {
				return;
			}

			this.uploading = true;

			try {
				const formData = new FormData();
				formData.append("file", this.fileList[0].raw);
                formData.append("country", this.selectedCountry);

				let response = await HTTP.post("v1/karen/settings/upload-agent-file", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				});

				console.log("Upload response:", response);
				Message.success("File uploaded successfully!");

				this.resetForm();
			} catch (error) {
				if (error.response && error.response.status === 422) {
					let errorMessage = error.response.data?.error.message || "Invalid file format or size. Please upload a valid XLSX file.";
					Message.error(errorMessage);
				} else {
					console.error("Upload error:", error);
				}

				this.resetForm();
			} finally {
				this.uploading = false;
			}
		},
		resetForm() {
			this.fileList = [];
			this.$refs.fileUpload.clearFiles();
		}
	},
};
</script>

<style lang="scss" scoped>
.karen-ai-files-manager {
	.card {
		border: 1px solid #e4e7ea;
		border-radius: 0.375rem;
		box-shadow: 0 0.15rem 1.75rem 0 rgba(33, 40, 50, 0.15);

		.card-body {
			padding: 1.25rem;
		}
	}

	.form-label {
		font-weight: 600;
		color: #32325d;
		margin-bottom: 0.5rem;
		display: block;
	}

	.w-100 {
		width: 100% !important;
	}
}

// Element UI Upload component styling
.upload-demo {
	.el-upload {
		width: 100%;
	}

	.el-upload-dragger {
		width: 100%;
		height: 180px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		border: 2px dashed #d9d9d9;
		border-radius: 6px;
		cursor: pointer;
		position: relative;
		overflow: hidden;
		transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

		&:hover {
			border-color: #409eff;
		}

		.el-icon-upload {
			font-size: 67px;
			color: #c0c4cc;
			margin-bottom: 16px;
			line-height: 50px;
		}

		.el-upload__text {
			color: #606266;
			font-size: 14px;
			text-align: center;

			em {
				color: #409eff;
				font-style: normal;
			}
		}
	}

	.el-upload__tip {
		font-size: 12px;
		color: #606266;
		margin-top: 7px;
	}
}
</style>
